'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { MeridianCompassMark } from './components/brand/MeridianCompassMark'

/**
 * Branded route error boundary. Next renders this instead of a blank page when
 * a page throws during render. The error itself is reported by the existing
 * client error buffer (IssueErrorBufferInit); this surface only needs to be
 * calm, on-brand, and recoverable.
 */
export default function RouteError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    console.error('[route-error]', error)
  }, [error])

  return (
    <div className="eq-field flex min-h-[70vh] items-center justify-center rounded-2xl px-4 py-16">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 w-fit">
          <MeridianCompassMark size={64} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: '#DFC06A' }}>
          Equinox · Meridian Anesthesia
        </p>
        <h1
          className="mt-2 text-3xl font-bold text-white"
          style={{ fontFamily: "Georgia, 'Times New Roman', serif", letterSpacing: '-0.018em' }}
        >
          Something went wrong on this page.
        </h1>
        <p className="mt-3 text-sm" style={{ color: '#D4D8DE' }}>
          Nothing you entered has been lost elsewhere in the platform. Try the page again, or head home.
          {error?.digest ? <span className="block mt-2 text-xs" style={{ color: '#8899AA' }}>Reference {error.digest}</span> : null}
        </p>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={reset}
            className="eq-press rounded-lg px-4 py-2.5 text-sm font-semibold"
            style={{ backgroundColor: '#DFC06A', color: '#091525' }}
          >
            Try again
          </button>
          <Link
            href="/"
            className="eq-press rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
            style={{ border: '1px solid rgba(255,255,255,0.18)', backgroundColor: 'rgba(255,255,255,0.06)' }}
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  )
}
